import { jsPDF } from 'jspdf';
import { ResumeData } from '../types/resume';

export const generatePDF = async (
  data: ResumeData,
  filename: string
): Promise<void> => {
  try {
    // Create PDF with A4 dimensions and better text rendering
    const pdf = new jsPDF({
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait',
      compress: false, // Disable compression for better text quality
      putOnlyUsedFonts: true
    });

    // Set better quality fonts
    pdf.setFont('helvetica', 'normal', 'normal');
    pdf.setFontSize(11);
    
    // Page margins in mm
    const margin = 20;
    const pageWidth = 210; // A4 width
    const pageHeight = 297; // A4 height
    const contentWidth = pageWidth - (margin * 2);
    
    // Current Y position
    let y = margin;
    let currentPage = 1;

    // Function to add new page
    const addNewPage = () => {
      pdf.addPage();
      currentPage++;
      y = margin;
      // Add header to new page if needed
      if (data.personalInfo?.fullName) {
        pdf.setFontSize(9);
        pdf.setTextColor(128, 128, 128);
        pdf.text(data.personalInfo.fullName, margin, 10);
        pdf.setFontSize(11);
        pdf.setTextColor(0, 0, 0);
      }
    };

    // Function to check if we need a new page
    const checkNewPage = (height: number) => {
      if (y + height > pageHeight - margin) {
        addNewPage();
        return true;
      }
      return false;
    };
    
    // Header section
    if (data.personalInfo) {
      // Name
      pdf.setFontSize(24); 
      pdf.setFont('helvetica', 'bold', 'bold');
      const name = data.personalInfo.fullName.toUpperCase();
      pdf.text(name, pageWidth / 2, y, { align: 'center' });
      y += 8;

      // Title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'normal', 'normal');
      pdf.text(data.personalInfo.title, pageWidth / 2, y, { align: 'center' });
      y += 6;

      // Contact info
      pdf.setFontSize(10);
      pdf.setTextColor(80, 80, 80);
      const contactInfo = [
        data.personalInfo.email,
        data.personalInfo.phone,
        data.personalInfo.location
      ].filter(Boolean);
      
      const contactText = contactInfo.join(' • ');
      pdf.text(contactText, pageWidth / 2, y, { align: 'center' });
      y += 10;

      // Links section if provided
      if (data.personalInfo.linkedin || data.personalInfo.website) {
        pdf.setFontSize(9);
        const links = [];
        if (data.personalInfo.linkedin) {
          links.push(`LinkedIn: ${data.personalInfo.linkedin}`);
        }
        if (data.personalInfo.website) {
          links.push(`Website: ${data.personalInfo.website}`);
        }
        pdf.text(links.join(' | '), pageWidth / 2, y, { align: 'center' });
        y += 8;
      }
      pdf.setTextColor(0, 0, 0);
    }

    // Summary
    if (data.personalInfo?.summary) {
      y += 4;
      checkNewPage(20); // Check if we need a new page for summary

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold', 'bold');
      pdf.setTextColor(70, 70, 70);
      pdf.text('Professional Summary', margin, y);
      y += 6;

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal', 'normal');
      pdf.setTextColor(0, 0, 0);
      const summaryLines = pdf.splitTextToSize(data.personalInfo.summary, contentWidth);
      const summaryHeight = summaryLines.length * 5;
      
      if (checkNewPage(summaryHeight)) {
        pdf.text(summaryLines, margin, y);
        y += summaryHeight;
      } else {
        pdf.text(summaryLines, margin, y);
        y += summaryHeight + 8;
      }
    }

    // Experience
    if (data.experience?.length) {
      checkNewPage(20); // Check for new page before section header

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold', 'bold');
      pdf.setTextColor(70, 70, 70);
      pdf.text('Professional Experience', margin, y);
      y += 6;

      pdf.setTextColor(0, 0, 0);
      data.experience.forEach(exp => {
        const expHeight = 20 + (exp.achievements.length * 5); // Estimate height
        if (checkNewPage(expHeight)) {
          y += 4; // Add some spacing after page break
        }

        // Position and dates
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold', 'bold');
        pdf.text(exp.position, margin, y);
        
        // Right-aligned dates
        pdf.setFont('helvetica', 'normal', 'normal');
        pdf.setTextColor(100, 100, 100);
        const dateText = `${exp.startDate} - ${exp.current ? 'Present' : exp.endDate}`;
        pdf.text(dateText, pageWidth - margin, y, { align: 'right' });
        y += 5;

        // Company and location
        pdf.setFontSize(11);
        pdf.text(`${exp.company} • ${exp.location}`, margin, y);
        y += 5;

        pdf.setTextColor(0, 0, 0);
        // Description
        const descLines = pdf.splitTextToSize(exp.description, contentWidth);
        if (checkNewPage(descLines.length * 5)) {
          y += 2;
        }
        pdf.text(descLines, margin, y);
        y += (descLines.length * 5);

        // Achievements
        if (exp.achievements.length) {
          y += 3;
          exp.achievements.forEach(achievement => {
            const lines = pdf.splitTextToSize(`• ${achievement}`, contentWidth - 5);
            if (checkNewPage(lines.length * 5)) {
              y += 2;
            }
            pdf.text(lines, margin + 5, y);
            y += (lines.length * 5);
          });
        }

        y += 8;
      });
    }

    // Education
    if (data.education?.length) {
      checkNewPage(20); // Check for new page before section

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold', 'bold');
      pdf.setTextColor(70, 70, 70);
      pdf.text('Education', margin, y);
      y += 6;

      pdf.setTextColor(0, 0, 0);
      data.education.forEach(edu => {
        const eduHeight = 15 + (edu.achievements.length * 5);
        if (checkNewPage(eduHeight)) {
          y += 4;
        }

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold', 'bold');
        pdf.text(edu.school, margin, y);
        pdf.setFont('helvetica', 'normal', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text(edu.graduationDate, pageWidth - margin, y, { align: 'right' });
        y += 5;

        pdf.setFontSize(11);
        pdf.setTextColor(0, 0, 0);
        pdf.text(`${edu.degree} in ${edu.field}`, margin, y);
        y += 5;

        if (edu.gpa) {
          pdf.setTextColor(100, 100, 100);
          pdf.text(`GPA: ${edu.gpa}`, margin, y);
          y += 5;
          pdf.setTextColor(0, 0, 0);
        }

        if (edu.achievements.length) {
          y += 2;
          edu.achievements.forEach(achievement => {
            const lines = pdf.splitTextToSize(`• ${achievement}`, contentWidth - 5);
            if (checkNewPage(lines.length * 5)) {
              y += 2;
            }
            pdf.text(lines, margin + 5, y);
            y += (lines.length * 5);
          });
        }

        y += 6;
      });
    }

    // Skills
    if (data.skills?.length) {
      checkNewPage(20); // Check for new page before section

      y += 4;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold', 'bold');
      pdf.setTextColor(70, 70, 70);
      pdf.text('Skills', margin, y);
      y += 6;

      // Group skills by category
      const skillsByCategory = data.skills.reduce((acc, skill) => {
        if (!acc[skill.category]) acc[skill.category] = [];
        acc[skill.category].push(skill.name);
        return acc;
      }, {} as Record<string, string[]>);

      Object.entries(skillsByCategory).forEach(([category, skills]) => {
        const skillText = skills.join(', ');
        const lines = pdf.splitTextToSize(`${category}: ${skillText}`, contentWidth);
        
        if (checkNewPage(lines.length * 5)) {
          y += 2;
        }

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(`${category}:`, margin, y);
        pdf.setFont('helvetica', 'normal', 'normal');
        pdf.text(skillText, margin + 25, y);
        y += (lines.length * 5) + 3;
      });
    }

    // Save PDF with metadata
    pdf.setProperties({
      title: `${data.personalInfo?.fullName || 'Resume'} - Professional Resume`,
      subject: 'Professional Resume',
      keywords: 'resume, cv, professional, career',
      creator: 'Make Resume Free',
      author: data.personalInfo?.fullName || 'Anonymous',
      modDate: new Date().toISOString(),
      creationDate: new Date().toISOString()
    });

    // Save the PDF
    pdf.save(filename);

  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};